import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AssistantChart } from './AssistantChart';
import { AssistantTable } from './AssistantTable';
import type { AiChatChart, AiChatTable } from './types';

/** Defined once: a new array per render would re-parse the document every time. */
const REMARK_PLUGINS = [remarkGfm];

/**
 * How the answer's markdown is rendered.
 *
 * These overrides are the whole safety story of rendering model output as
 * markup. `skipHtml` (on the element below) drops raw HTML rather than
 * sanitising it — an answer that emits a `<script>` gets nothing rendered,
 * which is the only outcome that needs no further argument. The rest is
 * shape:
 *
 * - Headings move down two levels. The answer is never the top of the page —
 *   it sits in a panel under a page heading — so left alone it would emit an
 *   `h1` beside the page's own and give a screen reader two roots.
 * - Tables get a scroll container, because an answer's table is as wide as the
 *   model felt like and the panel is 22.5rem.
 * - Images are not fetched. A markdown image is a request to a host the model
 *   chose, made from the customer's browser, and an answer has no business
 *   tracking anybody.
 */
const MARKDOWN_COMPONENTS: Components = {
  h1: ({ node: _node, ...rest }) => <h3 {...rest} />,
  h2: ({ node: _node, ...rest }) => <h4 {...rest} />,
  h3: ({ node: _node, ...rest }) => <h5 {...rest} />,
  h4: ({ node: _node, ...rest }) => <h6 {...rest} />,
  h5: ({ node: _node, ...rest }) => <h6 {...rest} />,
  h6: ({ node: _node, ...rest }) => <h6 {...rest} />,
  a: ({ node: _node, ...rest }) => <a {...rest} rel="noreferrer noopener" />,
  img: ({ node: _node, alt }) => (
    <span className="fo-answer__image-note">[Image: {alt ?? 'no description'}]</span>
  ),
  table: ({ node: _node, ...rest }) => (
    <div className="fo-answer__scroll" tabIndex={0} role="region" aria-label="Answer table">
      <table {...rest} />
    </div>
  ),
};

export interface AssistantAnswerProps {
  /**
   * The prose. Markdown: headings, lists, emphasis, inline code, blockquotes
   * and GFM tables are all rendered as markup rather than as punctuation.
   */
  readonly answer: string;
  /** The table the answer carried, when it carried one. */
  readonly table?: AiChatTable | null;
  /** The chart the answer carried, when it carried one. */
  readonly chart?: AiChatChart | null;
}

/**
 * A complete assistant answer: prose, then the table, then the chart.
 *
 * The order is fixed and the parts are optional, so an answer that carried
 * nothing but prose is a paragraph and an answer that carried all three reads
 * the same way every time it is asked. The table and the chart are the answer's
 * *evidence*, which is why they sit under the prose that refers to them rather
 * than beside it.
 *
 * Nothing here trusts its input. The prose is rendered as markup with raw HTML
 * dropped; the table and the chart each decide for themselves whether they can
 * be rendered coherently and render nothing when they cannot. A malformed
 * payload costs the reader a table, never the answer.
 *
 * @param props see {@link AssistantAnswerProps}
 * @returns the answer
 */
export function AssistantAnswer({ answer, table, chart }: AssistantAnswerProps): React.JSX.Element {
  return (
    <div className="fo-answer">
      {answer.trim() === '' ? null : (
        <div className="fo-answer__prose">
          <Markdown remarkPlugins={REMARK_PLUGINS} skipHtml components={MARKDOWN_COMPONENTS}>
            {answer}
          </Markdown>
        </div>
      )}
      {table === null || table === undefined ? null : <AssistantTable table={table} />}
      {chart === null || chart === undefined ? null : <AssistantChart chart={chart} />}
    </div>
  );
}
